import { ChangeDetectionStrategy, Component } from '@angular/core';
import { isNotNullish } from '@y42/util/nullish';
import { AgRendererComponent } from 'ag-grid-angular';
import { ICellRendererParams, IRowNode } from 'ag-grid-community';
import { GitFolderToUI } from '../../git-folder-to-ui.model';
import { Y42GitFolder } from '../../wasm-git-folders.model';
import { GitDiffTreeItem } from '../git-diff-tree.model';

type GitDiffTreeItemGroupCellRendererParams = ICellRendererParams<
  GitDiffTreeItem,
  GitDiffTreeItem['product'] | GitDiffTreeItem['typeDisplayName']
>;

@Component({
  selector: 'y42-git-diff-tree-item-group-cell-renderer',
  templateUrl: './git-diff-tree-item-group-cell-renderer.component.html',
  styleUrls: ['./git-diff-tree-item-group-cell-renderer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GitDiffTreeItemGroupCellRendererComponent implements AgRendererComponent {
  params!: GitDiffTreeItemGroupCellRendererParams;

  name?: string | null;

  icon?: string;

  get aggregationResult() {
    const field = this.params.node.field;
    const showRowGroup = this.params.colDef?.showRowGroup as string;

    const columns = this.params.columnApi.getAllColumns() || [];
    const groupIndex = columns.findIndex((col) => col.getColId() === field);
    const cellIndex = columns.findIndex((col) => col.getColId() === showRowGroup);
    if (cellIndex < groupIndex) {
      return null;
    }

    if (this.params.node.allLeafChildren) {
      const valueMapper = (node: IRowNode) => {
        if (showRowGroup.startsWith('level')) {
          const folderIndex = showRowGroup.match(/\d+/)![0];
          return node.data.folders[folderIndex];
        } else {
          return node.data[showRowGroup];
        }
      };
      const count = [...new Set(this.params.node.allLeafChildren.filter(isNotNullish).map(valueMapper).filter(Boolean))]
        .length;
      return `(${count})`;
    }

    return null;
  }

  agInit(params: GitDiffTreeItemGroupCellRendererParams): void {
    this.setData(params);
  }

  refresh(params: GitDiffTreeItemGroupCellRendererParams): boolean {
    this.setData(params);
    return false;
  }

  toggleOpen() {
    this.params.node.setExpanded(!this.params.node.expanded);
  }

  private setData(params: GitDiffTreeItemGroupCellRendererParams) {
    this.params = params;
    this.name = params.node.key;

    if (params.node.level === 0) {
      this.setFeatureDef(params);
    }

    if (params.node.group && params.value && params.node.level === 1) {
      if (params.node.allLeafChildren.length) {
        this.icon = params.node.allLeafChildren[0]?.data?.typeLogoUrl || '';
      }
    }
    if (!params.node.group && params.colDef?.field === 'type') {
      this.icon = params.node.data?.typeLogoUrl || '';
    }
  }

  private setFeatureDef(params: GitDiffTreeItemGroupCellRendererParams) {
    const key = [params.value, params.node.key].find(
      (featureType) => !!GitFolderToUI[featureType as Y42GitFolder],
    ) as Y42GitFolder;

    if (GitFolderToUI[key]) {
      this.name = GitFolderToUI[key].name;
      this.icon = GitFolderToUI[key].logo;
    }
  }
}
